import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useTypedSelector } from '@redux/Hooks/storeSelectors';
import { selectCurrentProjectId } from '@redux/selectors/selectors';
import { usePresetsContext } from '@context/presetsContext';
import { useToastContext } from '@context/toastContext';
import { isErrorResponse, safelyParseJson } from '@utils/utils';
import {
    lambdaOperationsApi,
    LambdaHistoryItem,
    LambdaOperation,
} from '@api/lambdaOperationsApi';
import { useLambdaOperations } from './hooks/useLambdaOperations';
import { useLambdaOperationMetadata } from './hooks/useLambdaOperationMetadata';
import { useLambdaHistory } from './hooks/useLambdaHistory';
import { useLambdaLayerToggle } from './hooks/useLambdaLayerToggle';
import AvailableOperationsList from './components/AvailableOperationsList';
import LambdaRunForm from './components/LambdaRunForm';
import LambdaHistory from './components/LambdaHistory';
import { getLambdaOperationTitle, openLambdaDashboard } from './lambdaOperations.utils';
import './lambda-operations.scss';

const LambdaOperationsPanel = () => {
    const [selectedOperation, setSelectedOperation] = useState<LambdaOperation | null>(null);
    const [starting, setStarting] = useState(false);
    const [restartingById, setRestartingById] = useState<Record<string, boolean>>({});
    const { selectedPresetId } = usePresetsContext();
    const [searchParams] = useSearchParams();
    const currentPresetId = selectedPresetId || searchParams.get('presetId') || '';
    const projectId = useTypedSelector(selectCurrentProjectId);
    const { addToast } = useToastContext();
    const { t } = useTranslation();
    const formSectionRef = useRef<HTMLElement | null>(null);

    const {
        operations,
        loading: operationsLoading,
        error: operationsError,
        retry: retryOperations,
    } = useLambdaOperations();
    const {
        metadata,
        loading: metadataLoading,
        error: metadataError,
    } = useLambdaOperationMetadata(selectedOperation, currentPresetId);
    const {
        history,
        loading: historyLoading,
        error: historyError,
        refresh: refreshHistory,
    } = useLambdaHistory(currentPresetId);
    const { checkedById, loadingById, toggleLayer } = useLambdaLayerToggle(currentPresetId);

    const startOperation = useCallback(
        async (payload: Record<string, unknown>) => {
            if (!selectedOperation) {
                return;
            }

            const abortController = new AbortController();
            const metadataPayload = {
                ...payload,
                ...(currentPresetId ? { presetId: currentPresetId } : {}),
            };
            setStarting(true);
            const resp = await lambdaOperationsApi.startOperation(
                selectedOperation,
                metadataPayload,
                abortController.signal
            );
            setStarting(false);

            if (resp && typeof resp === 'object' && isErrorResponse(resp)) {
                const parsedError =
                    typeof resp.error === 'string' ? safelyParseJson(resp.error) : resp.error;
                const errorMessage =
                    (parsedError && typeof parsedError === 'object' && 'error' in parsedError
                        ? String(parsedError.error)
                        : '') ||
                    resp.error_description ||
                    t('lambda-operations.failed-start');
                addToast(errorMessage, 'error');
                return;
            }

            addToast(
                t('lambda-operations.start-success', {
                    name: getLambdaOperationTitle(selectedOperation),
                }),
                'success'
            );
            refreshHistory();
        },
        [addToast, currentPresetId, refreshHistory, selectedOperation, t]
    );

    const restartCalculation = useCallback(
        async (item: LambdaHistoryItem) => {
            if (!item.isRestartable) {
                return;
            }

            const abortController = new AbortController();
            setRestartingById(prev => ({ ...prev, [item.id]: true }));
            const resp = await lambdaOperationsApi.restartCalculation({
                lambdaId: item.id,
                abortSignal: abortController.signal,
            });
            setRestartingById(prev => ({ ...prev, [item.id]: false }));

            if (resp && typeof resp === 'object' && isErrorResponse(resp)) {
                addToast(resp.error_description || t('lambda-operations.failed-restart'), 'error');
                return;
            }

            addToast(t('lambda-operations.restart-success'), 'success');
            refreshHistory();
        },
        [addToast, refreshHistory, t]
    );

    const openDashboard = useCallback(
        (item: LambdaHistoryItem) => {
            openLambdaDashboard(item, projectId);
        },
        [projectId]
    );

    const selectOperation = useCallback((operation: LambdaOperation) => {
        setSelectedOperation(operation);
        window.setTimeout(() => {
            formSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }, 0);
    }, []);

    return (
        <div className="lambda-operations-panel" data-testid="lambdaOperationsPanel">
            <section className="lambda-operations-section">
                <div className="lambda-section-heading">
                    <p>{t('lambda-operations.available-title')}</p>
                    <span>{t('lambda-operations.available-subtitle')}</span>
                </div>
                <AvailableOperationsList
                    operations={operations}
                    selectedOperation={selectedOperation}
                    loading={operationsLoading}
                    error={operationsError}
                    onSelect={selectOperation}
                    onRetry={retryOperations}
                />
            </section>
            <section className="lambda-operations-section" ref={formSectionRef}>
                {selectedOperation ? (
                    <LambdaRunForm
                        operation={selectedOperation}
                        metadata={metadata}
                        loading={metadataLoading}
                        starting={starting}
                        error={metadataError}
                        presetId={currentPresetId}
                        onSubmit={startOperation}
                    />
                ) : (
                    <div className="lambda-operations-empty">
                        {t('lambda-operations.select-operation')}
                    </div>
                )}
            </section>
            <section className="lambda-operations-section lambda-operations-section_history">
                <div className="lambda-section-heading">
                    <p>{t('lambda-operations.history-title')}</p>
                    <span>{t('lambda-operations.history-subtitle')}</span>
                </div>
                <LambdaHistory
                    history={history}
                    loading={historyLoading}
                    error={historyError}
                    checkedById={checkedById}
                    layerLoadingById={loadingById}
                    restartingById={restartingById}
                    onLayerToggle={toggleLayer}
                    onOpenDashboard={openDashboard}
                    onRestart={restartCalculation}
                />
            </section>
        </div>
    );
};

export default LambdaOperationsPanel;
